import { useState, useEffect } from "react";
import { t, font, Button, Panel, Avatar, StatCard, Badge, EmptyState } from "@realtyflows/ui";
import { getAppointments, createAppointment, updateAppointment, deleteAppointment, getClients } from "../api";
import ConfirmModal from "../components/ConfirmModal";
import useResponsive from "../hooks/useResponsive";

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ client_id: "", property: "", appointment_date: "", appointment_time: "", type: "Showing" });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const responsive = useResponsive();

  const fetchData = () => {
    Promise.all([getAppointments(), getClients()])
      .then(([a, l]) => { setAppointments(a); setClients(l); })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!form.client_id || !form.property || !form.appointment_date || !form.appointment_time) return;
    try { await createAppointment(form); setForm({ client_id: "", property: "", appointment_date: "", appointment_time: "", type: "Showing" }); fetchData(); } catch (err) { console.error(err); }
  };
  const handleStatusUpdate = async (id, status) => { try { await updateAppointment(id, { status }); fetchData(); } catch (err) { console.error(err); } };
  const handleDelete = async (id) => { try { await deleteAppointment(id); fetchData(); } catch (err) { console.error(err); } };

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading appointments...</div>;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard label="Total" value={appointments.length.toString()} delay="0s" />
        <StatCard label="Confirmed" value={appointments.filter(a => a.status === "confirmed").length.toString()} trend={`+${appointments.filter(a => a.status === "confirmed").length}`} delay="0.07s" />
        <StatCard label="Pending" value={appointments.filter(a => a.status === "pending").length.toString()} delay="0.14s" />
        <StatCard label="This Week" value={appointments.length.toString()} delay="0.21s" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "1fr" : "1fr 340px", gap: 20 }}>
        <div>
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 12, fontFamily: font.mono, letterSpacing: 1 }}>UPCOMING APPOINTMENTS</div>
          {appointments.map(apt => (
            <Panel key={apt.id} padding={20} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <Avatar name={apt.client_name} size={42} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{apt.client_name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Badge variant={apt.status === "confirmed" ? "emerald" : "amber"}>{apt.status}</Badge>
                      <div style={{ position: "relative" }}>
                        <div onClick={() => setOpenMenu(openMenu === apt.id ? null : apt.id)} style={{
                          width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", background: openMenu === apt.id ? t.surfaceWarm : "transparent", border: `1px solid ${openMenu === apt.id ? t.border : "transparent"}`,
                          transition: "all 0.15s",
                        }}
                          onMouseEnter={e => { if (openMenu !== apt.id) e.currentTarget.style.background = t.surfaceWarm; }}
                          onMouseLeave={e => { if (openMenu !== apt.id) e.currentTarget.style.background = "transparent"; }}
                        >
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="3" r="1.5" fill={t.textSecondary} />
                            <circle cx="8" cy="8" r="1.5" fill={t.textSecondary} />
                            <circle cx="8" cy="13" r="1.5" fill={t.textSecondary} />
                          </svg>
                        </div>
                        {openMenu === apt.id && (
                          <>
                            <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setOpenMenu(null)} />
                            <div style={{
                              position: "absolute", top: 32, right: 0, background: t.surface,
                              border: `1.5px solid ${t.border}`, borderRadius: 10, padding: "6px 0",
                              minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 100,
                              animation: "scaleIn 0.15s ease",
                            }}>
                              <div onClick={() => { setConfirmDelete({ id: apt.id, name: apt.property || apt.client_name }); setOpenMenu(null); }} style={{
                                padding: "9px 16px", display: "flex", alignItems: "center", gap: 10,
                                cursor: "pointer", fontSize: 13, fontFamily: font.body,
                                color: t.coral, transition: "background 0.1s",
                              }}
                                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <span style={{ fontSize: 14 }}>{"\uD83D\uDDD1\uFE0F"}</span>
                                <span>Cancel Appointment</span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: t.textSecondary, marginTop: 2, fontFamily: font.body }}>{apt.property}</div>
                  <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                    {[{ l: "Date", v: new Date(apt.appointment_date).toLocaleDateString() }, { l: "Time", v: apt.appointment_time }, { l: "Type", v: apt.type }].map(d => (
                      <div key={d.l} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{d.l}:</div>
                        <div style={{ fontSize: 12, color: d.l === "Type" ? t.emerald : t.textPrimary, fontFamily: d.l !== "Type" ? font.mono : font.body }}>{d.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.border}` }}>
                <Button variant="primary" size="sm" onClick={() => handleStatusUpdate(apt.id, "confirmed")}>Confirm</Button>
                <Button variant="secondary" size="sm" onClick={() => handleStatusUpdate(apt.id, "pending")}>Reschedule</Button>
              </div>
            </Panel>
          ))}
          {appointments.length === 0 && <Panel><EmptyState title="No appointments yet" sub="Schedule a showing from the form" /></Panel>}
        </div>

        <div>
          <Panel padding={18}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>Schedule New Appointment</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Client</div>
              <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} style={{ width: "100%", background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 6, padding: "8px 12px", color: t.textPrimary, fontSize: 12, outline: "none", fontFamily: font.body }}>
                <option value="">Select a client...</option>
                {clients.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Property Address</div>
              <input value={form.property} onChange={e => setForm({ ...form, property: e.target.value })} placeholder="Enter address..."
                style={{ width: "100%", boxSizing: "border-box", background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 6, padding: "8px 12px", color: t.textPrimary, fontSize: 12, outline: "none", fontFamily: font.body }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <div><div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Date</div><input type="date" value={form.appointment_date} onChange={e => setForm({ ...form, appointment_date: e.target.value })} style={{ width: "100%", boxSizing: "border-box", background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 6, padding: "8px 12px", color: t.textPrimary, fontSize: 12, outline: "none", fontFamily: font.body }} /></div>
              <div><div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Time</div><input type="time" value={form.appointment_time} onChange={e => setForm({ ...form, appointment_time: e.target.value })} style={{ width: "100%", boxSizing: "border-box", background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 6, padding: "8px 12px", color: t.textPrimary, fontSize: 12, outline: "none", fontFamily: font.body }} /></div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Type</div>
              <div style={{ display: "flex", gap: 8 }}>
                {["Showing", "Consultation", "Open House"].map(tp => (
                  <div key={tp} onClick={() => setForm({ ...form, type: tp })}
                    style={{ padding: "6px 12px", borderRadius: 6, background: form.type === tp ? t.emeraldLight : t.surfaceWarm, border: `1px solid ${form.type === tp ? t.emerald : t.border}`, fontSize: 11, color: form.type === tp ? t.emerald : t.textSecondary, cursor: "pointer", fontFamily: font.body }}>{tp}</div>
                ))}
              </div>
            </div>
            <Button variant="primary" size="sm" fullWidth onClick={handleCreate}>Schedule Appointment</Button>
          </Panel>
        </div>
      </div>
      {confirmDelete && (
        <ConfirmModal
          title="Cancel Appointment"
          message={`Are you sure you want to cancel the appointment for "${confirmDelete.name}"?`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => { handleDelete(confirmDelete.id); setConfirmDelete(null); }}
        />
      )}
    </div>
  );
}
